import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import AOIChooser from '../../components/intertidal/AOIChooser';
import ImageListRefiner from '../../components/intertidal/ImageListRefiner';
import IntertidalZoneDefiner from '../../components/intertidal/IntertidalZoneDefiner';
import VectorEditor from '../../components/intertidal/VectorEditor';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import Footer from '../../components/core/Footer';
import { Actions as Auth } from '../../../store/ducks/auth';
import { FIRST, useStepNavigations } from '../../../hooks/useStepNavigations';

/**
 * Module for the intertidal process
 * @module Intertidal
 */

/**
 * @constant MODULE_ACCENT
 * @description Intertidal's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:Intertidal
 */
const MODULE_ACCENT = '#FDC669';

const STEPS = [
  {
    label: 'forms.intertidal.1.title',
    content: 'forms.intertidal.1.description',
    /**
     * Wrapped rather than passed straight through: `SatelliteChooser` is shared by every module and
     * `makeRoutes` renders it with a fixed set of props, so the redesigned look is opted into here.
     */
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.intertidal.2.title',
    content: '',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    label: 'forms.intertidal.3.title',
    content: 'forms.intertidal.3.description',
    requires: 'satellite',
    component: ImageListRefiner,
  },
  {
    label: 'forms.intertidal.4.title',
    content: 'forms.intertidal.4.description',
    requires: 'geometry',
    component: IntertidalZoneDefiner,
  },
  {
    label: 'forms.intertidal.5.title',
    content: 'forms.intertidal.5.description',
    component: VectorEditor,
  },
];

const VISUAL_STEPS = [
  { label: 'forms.intertidal.1.title', realIndex: 0 },
  { label: 'forms.intertidal.2.title', realIndex: 1 },
  { label: 'forms.intertidal.3.title', realIndex: 2 },
  { label: 'forms.intertidal.validationStep.title', realIndex: 3 },
];

/**
 * @function IntertidalPage
 * @description Main component for the Intertidal acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link AOIChooser} - Component for defining area of interest (AOI)
 * @see {@link ImageListRefiner} - Component for refining image list
 * @see {@link IntertidalZoneDefiner} - Component for defining the intertidal zone
 * @see {@link VectorEditor} - Component for editing the vectorized intertidal zone
 * **Called by:
 * @see {MainPage} - via the `/intertidal/:step?` Route (imported as Intertidal).
 * @memberof module:Intertidal
 * @returns {JSX.Element} Complete acquisition page JSX element
 */
const IntertidalPage = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const { acquisitionData, navigate, navigateTo, stepAttendsRequirements, canRevisitStep, makeRoutes } = useStepNavigations(
    match,
    STEPS,
    `/main/intertidal/result`,
  );

  useEffect(() => {
    dispatch(Auth.loadClientAuth());
  }, []);

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;
  const visualStep = Math.min(step, VISUAL_STEPS.length - 1);

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.intertidal.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={VISUAL_STEPS.map((el, i) => ({
                id: String(i),
                label: t(el.label),
                disabled: !canRevisitStep(el.realIndex),
              }))}
              currentStep={String(visualStep)}
              onStepClick={(i) =>
                canRevisitStep(VISUAL_STEPS[i].realIndex) && navigateTo(VISUAL_STEPS[i].realIndex)
              }
            />
            <StepSummary acquisition={acquisitionData} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            <Switch>{makeRoutes(hasStep)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * intertidal acquisition wizard.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Intertidal
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  /**
   * The wizard header overlaps the card above it, so the indicator needs its own top room; the
   * max-width keeps the connectors short and centred instead of running the full card width.
   */
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));

export default IntertidalPage;
